import { DependencyList, useEffect, useState } from 'react'

import { AsyncEffectHandle, useAsyncEffectGenerator } from './async-effect'

// An item or a function that return an item given the list of all items, if any.
// Naturally, it is not possible to replace an item in an empty list.
declare type ItemFinderFn<T> = (state: T[]) => T | undefined
declare type ItemFinder<T> = T | ItemFinderFn<T>
// Assess how to resolve the item finder and then return the item.
// The item finder is the item itself or a function that returns the item.
const resolveItemFinder = <T>(itemFinder?: ItemFinder<T>, data?: T[]) =>
    typeof itemFinder === 'function' ? (itemFinder as ItemFinderFn<T>)(data ?? []) : itemFinder

// Compare id as the default item finder.
// Coerce T to have an `id` field, which is basically always the case.
// This approach is probably more readable than trying to express T as an object that may or may not have an `id` field.
// If T does in fact not have an id field, the developer will receive a warning.
// This function is curried such that is it returns an ItemFinder given the item to match.
// This is also why we ignore prettier here because it does not make the curried signature prettier...
// prettier-ignore
export const getFindById = <T>(item: T) => (data: T[]) => data.find((i) => {
    const a = item as { id: string }
    const b = i as { id: string }
    if (!(a.id && b.id)) {
        console.warn(
            'Implicite `id` comparison used by item finder in mutable state replacer, but an object were missing an id. Objects are printed above.\n' +
                'If this is intended, supply a custom item finder to the replacer function.'
        )
    }
    return a?.id === b?.id
})

declare type MutableListHandle<T> = [
    T[],
    ...AsyncEffectHandle,
    // A function that adds an element to the data.
    (item: T) => void,
    // A function that removes an element from the data.
    (item: T) => void,
    // A function that replaces the old item with a new one.
    (newItem: T, itemFinder?: ItemFinder<T>) => void,
]

/**
 * Expose a reusable, mutable structure with mutation handles.
 * Internally, a useEffect is used to synchronize with the deps changing.
 * However, it can be useful to keep the structure and add to or remove from it
 *  instead of sending another request for the latest representation.
 * For that purpose, this hook exposes add and remove functions,
 *  which do not repeat the effect but merely alters the local structure.
 * Sometimes it may be convenient to short-curcuit the effect, e.g. if dependencies are missing.
 *  In this case, it is possible to return specifically `null` from the effect, regardless of the model type.
 *  This tells the internal effect that the async action did not finalize and loading should persist according to declared initialization.
 *
 * @param {Function} effect the useEffect handler
 * @param {DependencyList} deps typical useEffect deps
 * @returns {Array} a handle array with capabilities in the following order:
 *  0=data, 1=loading, 2=error, 3=refresh, 4=add, 5=remove, 6=replace
 */
export const useMutableList = <T>(
    effect: () => Promise<T[] | null>,
    deps: DependencyList,
    initialLoadingState: boolean = false
): MutableListHandle<T> => {
    const [data, setData] = useState<T[]>([])

    const [loading, error, refresh] = useAsyncEffectGenerator(async () => {
        const result = await effect()
        // Signal whether to keep the initial loading state if the array is still empty.
        // This is useful for extending the loading state when many dependencies call the effect before it is ready.
        if (result === null) return { loadingSignal: initialLoadingState }
        setData(result)
    }, initialLoadingState)

    useEffect(() => {
        refresh()
    }, deps)

    const add = (...items: T[]) => setData((state) => [...state, ...items])
    const remove = (...items: T[]) => setData((state) => state.filter((i) => !items.includes(i)))
    const replace = (newItem: T, itemFinder: ItemFinder<T> = getFindById(newItem)) =>
        setData((state) => {
            const updated = [...state]
            const oldItem = resolveItemFinder(itemFinder, state)
            if (oldItem) {
                const index = updated.indexOf(oldItem)
                updated.splice(index, 1, newItem)
            }
            return updated
        })

    return [data, loading, error, refresh, add, remove, replace]
}

declare type MutableObjectHandle<T> = [
    T | undefined,
    ...AsyncEffectHandle,
    // A function that replaces the item with a new one.
    (item: T) => void,
]

/**
 * Expose a mutable, reusable object-state, which is useful for representing a changing entity.
 * Internally, a useEffect is used to synchronize with the deps changing.
 *
 * @param {Function} effect the useEffect handler
 * @param {DependencyList} deps typical useEffect deps
 * @returns {Array} a handle array with capabilities in the following order:
 *  0=data, 1=loading, 2=error, 3=refresh, 4=replace
 */
export const useMutableObject = <T>(
    effect: () => Promise<T | null>,
    deps: DependencyList,
    initialLoadingState: boolean = false
): MutableObjectHandle<T> => {
    const [data, setData] = useState<T>()

    const [loading, error, refresh] = useAsyncEffectGenerator(async () => {
        const result = await effect()
        if (result === null) return { loadingSignal: initialLoadingState }
        setData(result)
    }, initialLoadingState)

    useEffect(() => {
        refresh()
    }, deps)

    const replace = (item: T) => setData(item)

    return [data, loading, error, refresh, replace]
}
